import React from 'react'
import './carousel.scss'

const Carousel = ({ items, bg, hover }) => {
    const cursor = {
        x: 0, 
        dx: 0, 
        dragging: false,
        moved: false,
        startX: 0,
        threshold: 5
    }
    
    let animationId;
    let actualLeft = -100 / 3
    let actualRight = -100 / 3
    let actualSpeed = 0
    let isStop = false

    let force = 0
    let bounceMaxForce = 0.8
    let maxForce = 0

    const speed = .01
    const stepSpeed = 0.0002

    const carousel = React.useRef()
    const carouselLeft = React.useRef()
    const carouselRight = React.useRef()

    const minmax = (min, value, max) => Math.min(Math.max(min, value), max)

    const animate = () => {
        animationId = requestAnimationFrame(animate);
        if(!carouselLeft?.current || !carouselRight?.current) return
        carouselLeft.current.style.transform = `translate(${actualLeft}%)`
        carouselRight.current.style.transform = `translate(${actualRight}%)`

        actualLeft -= actualSpeed
        actualRight += actualSpeed

        actualSpeed += isStop ? -stepSpeed : stepSpeed
        actualSpeed = minmax(0, actualSpeed, speed)
        actualLeft += force
        actualRight += force
        force += (maxForce - force) / 10
        maxForce /= 1.4

        if (actualLeft <= (-100 / 3) * 2) {
            actualLeft = -100 / 3
        } else if (actualLeft >= 0) {
            actualLeft = -100 / 3
        }

        if (actualRight <= (-100 / 3) * 2) {
            actualRight = -100 / 3
        } else if (actualRight >= 0) {
            actualRight = -100 / 3
        }
    };

    const getClientX = (e) => {
        if (e.touches) {
            return e.touches[0].clientX;
        }
        return e.clientX;
    };

    const handleDragStart = (e) => {
        e.preventDefault();
        
        const rect = carousel.current.getBoundingClientRect()
        const clientX = getClientX(e)
        const offsetX = clientX - rect.left
        
        cursor.dragging = true
        cursor.moved = false
        cursor.startX = offsetX
        cursor.x = offsetX
        
        carousel.current.style.cursor = 'grabbing'
        carousel.current.style.userSelect = 'none'
        
        if (e.target.closest('a')) {
            e.preventDefault()
        }
    }

    const handleDragMove = (e) => {
        if (!cursor.dragging) return
        
        e.preventDefault()
        
        const rect = carousel.current.getBoundingClientRect()
        const clientX = getClientX(e)
        const offsetX = clientX - rect.left
        cursor.dx = offsetX - cursor.x
        cursor.x = offsetX
        
        if (Math.abs(offsetX - cursor.startX) > cursor.threshold) {
            cursor.moved = true
            maxForce = minmax(-bounceMaxForce, cursor.dx / 8, bounceMaxForce)
        }
    }

    const handleDragEnd = (e) => {
        cursor.dragging = false
        carousel.current.style.cursor = 'grab'
        carousel.current.style.userSelect = 'auto'
    }

    const handleLinkClick = (e, link) => {
        if (cursor.moved) {
            e.preventDefault()
            e.stopPropagation()
        }
    }

    React.useEffect(() => {
        animationId = requestAnimationFrame(animate)

        const handleMouseEnter = () => {
            if (!cursor.dragging) {
                carousel.current.style.cursor = 'grab'
            }
            isStop = true
        }

        const handleMouseLeave = () => {
            carousel.current.style.cursor = 'default'
            isStop = false
        }

        const element = carousel.current;
        
        element.addEventListener('mouseenter', handleMouseEnter)
        element.addEventListener('mouseleave', handleMouseLeave)
        element.addEventListener('mousedown', handleDragStart)
        
        element.addEventListener('touchstart', handleDragStart, { passive: false })
        element.addEventListener('touchmove', handleDragMove, { passive: false })
        element.addEventListener('touchend', handleDragEnd)
        element.addEventListener('touchcancel', handleDragEnd)
        
        window.addEventListener('mousemove', handleDragMove)
        window.addEventListener('mouseup', handleDragEnd)

        return () => {
            if (animationId) cancelAnimationFrame(animationId);
            
            element.removeEventListener('mouseenter', handleMouseEnter)
            element.removeEventListener('mouseleave', handleMouseLeave)
            element.removeEventListener('mousedown', handleDragStart)
            
            element.removeEventListener('touchstart', handleDragStart)
            element.removeEventListener('touchmove', handleDragMove)
            element.removeEventListener('touchend', handleDragEnd)
            element.removeEventListener('touchcancel', handleDragEnd)
            
            window.removeEventListener('mousemove', handleDragMove);
            window.removeEventListener('mouseup', handleDragEnd);
        }
    }, [])

    return (
        <>
            <div ref={carousel} className='carousel-wrapper' style={{ '--carousel-hover': hover, '--carousel-bg': bg }}>
                <div ref={carouselLeft} className='carousel-line left'>
                    {[1, 2, 3].map((num) => (
                        <div key={`part-${num}`} className='carousel-part'>
                            {items && items.slice(0, Math.ceil(items.length / 2)).map((item, index) => (
                                <a 
                                    key={`left-${num}-${index}`} 
                                    href={item.link} 
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='carousel-item'
                                    onClick={(e) => handleLinkClick(e, item.link)}
                                >
                                    <img src={item.image} alt="" />
                                </a>
                            ))}
                        </div>
                    ))}
                </div>
                <div ref={carouselRight} className='carousel-line right'>
                    {[1, 2, 3].map((num) => (
                        <div key={`part-${num}`} className='carousel-part'>
                            {items && items.slice(Math.ceil(items.length / 2)).map((item, index) => (
                                <a 
                                    key={`right-${num}-${index}`} 
                                    href={item.link} 
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='carousel-item'
                                    onClick={(e) => handleLinkClick(e, item.link)}
                                >
                                    <img src={item.image} alt="" />
                                </a>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </>
    )
}

export default Carousel